// based on https://altrim.io/posts/axios-http-client-using-typescript
import type { AxiosInstance, AxiosRequestConfig, AxiosResponse, InternalAxiosRequestConfig } from "axios";
import axios, { AxiosError } from "axios";
import * as Sentry from "@sentry/vue";
import { describeToken } from "@/helper/jwt";
import { clearMaster } from "@/helper/passwordKeyStore";
import { getApiUrl } from "@/data/constants";
import router from "@/router";

enum StatusCode {
  Unauthorized = 401,
  Forbidden = 403,
  TooManyRequests = 429,
  InternalServerError = 500,
}

const headers: Readonly<Record<string, string | boolean>> = {
  Accept: "application/json",
  "Content-Type": "application/json; charset=utf-8",
  "Access-Control-Allow-Credentials": true,
  "X-Requested-With": "XMLHttpRequest",
};

// We can use the following function to inject the JWT token through an interceptor
// We get the `accessToken` from the localStorage that we set when we authenticate
let lastReportedToken: string | null = null;

// Never log the token, `config.headers` or `config`: they carry the full `Authorization` header, and console logs become Sentry breadcrumbs.
export const injectToken = (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
  const token = localStorage.getItem("accessToken");

  if (token != null) {
    config.headers.Authorization = `Bearer ${token}`;
    const { redacted, scopes, expiresAt, expiresInSeconds } = describeToken(token);
    console.log(`injecting token ${redacted} into ${config.method?.toUpperCase()} ${config.url}`, { scopes, expiresAt, expiresInSeconds });
    if (token !== lastReportedToken) {
      lastReportedToken = token;
      Sentry.setContext("auth_token", { redacted, scopes, expiresAt });
    }
  } else {
    console.log(`no token to inject into ${config.method?.toUpperCase()} ${config.url}`);
  }
  return config;
};

class Http {
  private instance: AxiosInstance | null = null;

  private get http(): AxiosInstance {
    return this.instance != null ? this.instance : this.initHttp();
  }

  initHttp() {
    const http = axios.create({
      baseURL: getApiUrl(),
      headers,
      withCredentials: true,
    });

    http.interceptors.request.use(injectToken, (error) =>
      Promise.reject(error)
    );

    http.interceptors.response.use(
      (response) => response,
      async (error) => {
        return await this.handleError(error.response, error);
      }
    );

    this.instance = http;
    return http;
  }

  request<T = any, R = AxiosResponse<T>>(
    config: AxiosRequestConfig
  ): Promise<R> {
    return this.http.request(config);
  }

  get<T = any, R = AxiosResponse<T>>(
    url: string,
    config?: AxiosRequestConfig
  ): Promise<R> {
    return this.http.get<T, R>(url, config);
  }

  post<T = any, R = AxiosResponse<T>>(
    url: string,
    data?: T,
    config?: AxiosRequestConfig
  ): Promise<R> {
    return this.http.post<T, R>(url, data, config);
  }

  put<T = any, R = AxiosResponse<T>>(
    url: string,
    data?: T,
    config?: AxiosRequestConfig
  ): Promise<R> {
    return this.http.put<T, R>(url, data, config);
  }

  delete<T = any, R = AxiosResponse<T>>(
    url: string,
    config?: AxiosRequestConfig
  ): Promise<R> {
    return this.http.delete<T, R>(url, config);
  }

  patch<T = any, R = AxiosResponse<T>>(
    url: string,
    data?: T,
    config?: AxiosRequestConfig
  ): Promise<R> {
    return this.http.patch<T, R>(url, data, config);
  }

  // Handle global app errors
  // We can handle generic app errors depending on the status code
  async handleError(error: AxiosResponse | undefined, cause?: unknown) {
    if (error === undefined) {
      // No response at all: network failure, CORS, abort or timeout. Keep the original error, there is no status to handle.
      return Promise.reject(cause instanceof Error ? cause : new Error(`Request failed without a response: ${String(cause)}`));
    }
    const { status } = error;

    switch (status) {
      case StatusCode.InternalServerError: {
        // Handle InternalServerError
        break;
      }
      case StatusCode.Forbidden: {
        // Handle Forbidden
        break;
      }
      case StatusCode.Unauthorized: {
        console.log(`Unauthorized: ${JSON.stringify(error.data)}`);
        console.log(
          "Unauthorized, probably session token expired. Redirecting to the Login page."
        );
        await clearMaster();
        await router.push({ name: "Login" });
        break;
      }
      case StatusCode.TooManyRequests: {
        // Handle TooManyRequests
        break;
      }
    }
    const err = new Error(`${error.status} ${error.statusText}: ${JSON.stringify(error.data)}`);
    (err as any).response = error;
    return Promise.reject(err);
  }
}

export const http = new Http();
